import type { ViewerId } from "../data/model";
import type { Repository } from "../data/repository";
import type { LLMProvider } from "./provider";
import { CANONICAL_QUALITIES } from "../data/canonical";
import { domainsInText, peopleMatchingDomains } from "../data/domains";
import { PACKS } from "../data/packs";
import {
  ASSEMBLE_PERSON_LIMIT,
  assemblePrompt,
  buildRecords,
  buildStats,
  parseCanonicalSuggestion,
  parseProposedDimensions,
  proposeDimensionsPrompt,
  recordsToText,
  selectRelevant,
  sharpenPrompt,
  suggestCanonicalPrompt,
  surfacePrompt,
  type CanonicalSuggestion,
  type PersonRecord,
  type ProposedDimension,
} from "./prompts";

// One calibration question for a single read. The caller saves the answer into
// the rationale; SHARPEN itself writes nothing.
export function sharpen(
  provider: LLMProvider,
  personName: string,
  dimensionName: string,
  rationale: string,
): Promise<string> {
  return provider.complete(sharpenPrompt(personName, dimensionName, rationale));
}

async function recordsFor(
  repo: Repository,
  viewer: ViewerId,
  opts: { dimensionIds?: string[]; personIds?: Set<string> } = {},
): Promise<PersonRecord[]> {
  const [allPeople, dimensions, allTags, instances] = await Promise.all([
    repo.listPeople(),
    repo.listDimensions(),
    repo.listTags({ viewer_id: viewer }),
    repo.listInstances({ viewer_id: viewer }),
  ]);
  // Purpose scope: when given, keep only tags on the chosen purpose's
  // dimensions. People left with no in-scope tag drop out of the corpus.
  const tags = opts.dimensionIds
    ? allTags.filter((t) => opts.dimensionIds!.includes(t.dimension_id))
    : allTags;
  // Domain intersection: when a domain filter applies, keep only people flagged
  // with it. Dimension and domain stay separate axes; this narrows people, the
  // ranking still comes from the tags.
  const people = opts.personIds
    ? allPeople.filter((p) => opts.personIds!.has(p.id))
    : allPeople;
  return buildRecords(people, dimensions, tags, instances, Date.now());
}

// PROPOSE qualities for a stated role. Returns the parsed proposal only; it
// writes NOTHING. The caller shows it to edit and confirm; only an explicit
// confirm seeds dimensions. A couple of packs seed the prompt as exemplars.
export async function proposeDimensions(
  provider: LLMProvider,
  role: string,
): Promise<ProposedDimension[]> {
  const exemplars = PACKS.slice(0, 2).map((p) => ({
    label: p.label,
    rows: p.dimensions,
  }));
  const canonical = CANONICAL_QUALITIES.map((c) => ({ key: c.key, name: c.name }));
  const text = await provider.complete(proposeDimensionsPrompt(role, exemplars, canonical));
  return parseProposedDimensions(text);
}

// SUGGEST the single nearest backbone key for a custom quality (or null), with a
// reason. Returns the parsed suggestion only; it writes NOTHING and is merely
// preselected in the UI for the user to confirm or change.
export async function suggestCanonical(
  provider: LLMProvider,
  name: string,
  definition: string,
): Promise<CanonicalSuggestion> {
  const canonical = CANONICAL_QUALITIES.map((c) => ({
    key: c.key,
    name: c.name,
    definition: c.definition,
  }));
  const text = await provider.complete(suggestCanonicalPrompt(name, definition, canonical));
  return parseCanonicalSuggestion(text);
}

// Patterns and blind spots across the whole RATED set (untagged people are not
// sent), the user's data only.
export async function surface(
  repo: Repository,
  provider: LLMProvider,
  viewer: ViewerId,
): Promise<string> {
  const [people, dimensions, tags, instances] = await Promise.all([
    repo.listPeople(),
    repo.listDimensions(),
    repo.listTags({ viewer_id: viewer }),
    repo.listInstances({ viewer_id: viewer }),
  ]);
  const records = recordsToText(buildRecords(people, dimensions, tags, instances, Date.now()));
  const corpus = `${records}\n\n${buildStats(people, dimensions, tags)}`;
  return provider.complete(surfacePrompt(corpus));
}

// A reasoning brief for a decision, drawn only from stored judgements. At scale
// the corpus is narrowed to the people most relevant to the decision. The call
// and its output are logged to decisions.
export async function assemble(
  repo: Repository,
  provider: LLMProvider,
  viewer: ViewerId,
  decision: string,
  dimensionIds?: string[],
  domainTopicIds?: string[],
): Promise<string> {
  // Domain filter: an explicit selection from the UI, else any domain named in
  // the decision prompt. Intersected with the dimension ranking below.
  const [topics, personTopics] = await Promise.all([
    repo.listTopics(),
    repo.listPersonTopics(),
  ]);
  const domainIds =
    domainTopicIds && domainTopicIds.length
      ? domainTopicIds
      : domainsInText(topics, decision).map((t) => t.id);
  const personIds = domainIds.length
    ? peopleMatchingDomains(personTopics, domainIds)
    : undefined;

  const records = await recordsFor(repo, viewer, { dimensionIds, personIds });
  const { selected, truncated } = selectRelevant(records, decision, ASSEMBLE_PERSON_LIMIT);
  const output = await provider.complete(
    assemblePrompt(
      recordsToText(selected),
      decision,
      truncated ? { shown: selected.length, total: records.length } : undefined,
    ),
  );
  await repo.addDecision(decision, output, selected.map((r) => r.id));
  return output;
}
